/**
 * The plain frame (ADR 0035): eyebrow, the page `h1`, one meta line, and a
 * trailing slot for at most one quiet act and the page's filled act. No cover,
 * no mark, no hue: people and admissions open with it (ADR 0034).
 *
 * Inside the registrar workspace frame, which owns the page `h1`, the title
 * steps down to an `h2`, as `AcademicPageHeader` does.
 */
import type { ReactNode } from "react";

import { useRegistrarWorkspaceFrame } from "@/components/registrar/use-registrar-workspace-frame";
import { cn } from "@/lib/utils";

/** The eyebrow, title and meta column shared by the frame and the edition band. */
export function FrameTitle({
  eyebrow,
  title,
  meta,
  className,
}: {
  eyebrow?: ReactNode;
  title: ReactNode;
  meta?: ReactNode;
  className?: string;
}) {
  const inRegistrarWorkspace = useRegistrarWorkspaceFrame();
  const Heading = inRegistrarWorkspace ? "h2" : "h1";
  return (
    <div className={cn("flex min-w-0 flex-1 basis-48 flex-col gap-1", className)}>
      {eyebrow && <p className="eyebrow">{eyebrow}</p>}
      <Heading
        className={cn(
          "min-w-0 break-words text-foreground",
          inRegistrarWorkspace ? "text-tile-title font-semibold" : "text-title font-medium",
        )}
      >
        {title}
      </Heading>
      {meta && <p className="text-meta tabular-nums text-muted-foreground">{meta}</p>}
    </div>
  );
}

export function FrameHeader({
  eyebrow,
  title,
  meta,
  quietAction,
  action,
  className,
}: {
  eyebrow?: ReactNode;
  title: ReactNode;
  meta?: ReactNode;
  /** At most one quiet act (a subtle or outline button). */
  quietAction?: ReactNode;
  /** The page's one filled act, when the page has one. */
  action?: ReactNode;
  className?: string;
}) {
  return (
    <header data-slot="page-frame" className={cn("flex min-w-0 flex-wrap items-end gap-x-5 gap-y-3", className)}>
      <FrameTitle eyebrow={eyebrow} title={title} meta={meta} />
      {(quietAction || action) && (
        <div className="flex shrink-0 flex-wrap items-center gap-2">
          {quietAction}
          {action}
        </div>
      )}
    </header>
  );
}
